import { createRouter, createWebHistory } from '@ionic/vue-router'
import { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
// HIDDEN: Onboarding disabled - to be implemented later
// import { needsOnboarding } from '@/utils/onboarding'
import { i18n } from '@/plugins/i18n'
import { storageService } from '@/services/storage'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Root',
    component: () => import('@/views/LoadingView.vue'), // We'll create a loading view
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/auth/',
    component: () => import('@/views/auth/AuthLayout.vue'),
    children: [
      {
        path: '',
        redirect: '/auth/login'
      },
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: {
          requiresGuest: true,
          title: 'auth.signIn'
        }
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: {
          requiresGuest: true,
          title: 'auth.createAccount'
        }
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: {
          requiresGuest: true,
          title: 'auth.resetPassword'
        }
      },
      {
        path: 'reset-password/:uid/:token',
        name: 'ResetPassword',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: {
          requiresGuest: false,
          title: 'auth.setNewPassword'
        }
      }
    ]
  },
  // HIDDEN: Onboarding route disabled - to be implemented later
  // {
  //   path: '/onboarding',
  //   name: 'Onboarding',
  //   component: () => import('@/views/onboarding/OnboardingView.vue'),
  //   meta: {
  //     requiresAuth: true,
  //     title: 'onboarding.welcome'
  //   }
  // },
  {
    path: '/tabs/',
    component: () => import('@/views/tabs/TabsLayout.vue'),
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: '',
        redirect: '/tabs/home'
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/tabs/HomeView.vue'),
        meta: {
          title: 'navigation.home'
        }
      },
      /* MATT-GEOLOCATION-DISABLED: Track tab route
      {
        path: 'track',
        name: 'Track',
        component: () => import('@/views/tabs/TrackView.vue'),
        meta: {
          title: 'navigation.track'
        }
      },
      */
      {
        path: 'explore',
        name: 'Explore',
        component: () => import('@/views/tabs/ExploreView.vue'),
        meta: {
          title: 'navigation.explore'
        }
      },
      {
        path: 'community',
        name: 'Community',
        component: () => import('@/views/tabs/CommunityView.vue'),
        meta: {
          title: 'navigation.community'
        }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/tabs/ProfileView.vue'),
        meta: {
          title: 'navigation.profile'
        }
      },
      // Sub-pages: rendered inside TabsLayout's ion-router-outlet so swipe-back works on iOS
      {
        path: 'profile/edit',
        name: 'EditProfile',
        component: () => import('@/views/profile/EditProfileView.vue'),
        meta: {
          title: 'profile.editProfile'
        }
      },
      {
        path: 'poi/create',
        name: 'CreatePOI',
        component: () => import('@/views/poi/CreatePOIView.vue'),
        meta: {
          title: 'pois.addNewPOI'
        }
      },
      {
        path: 'poi/:id',
        name: 'POIDetail',
        component: () => import('@/views/poi/POIDetailView.vue'),
        meta: {
          title: 'pois.poiDetails'
        }
      },
      {
        path: 'poi/:id/edit',
        name: 'EditPOI',
        component: () => import('@/views/poi/CreatePOIView.vue'),
        meta: {
          title: 'pois.editPOI'
        }
      }
    ]
  },
  // Redirects for old paths (deep links / bookmarks backward compat)
  {
    path: '/profile/edit',
    redirect: '/tabs/profile/edit'
  },
  {
    path: '/poi/create',
    redirect: '/tabs/poi/create'
  },
  {
    path: '/poi/:id',
    redirect: (to) => `/tabs/poi/${to.params.id}`
  },
  {
    path: '/poi/:id/edit',
    redirect: (to) => `/tabs/poi/${to.params.id}/edit`
  },
  /* MATT-GEOLOCATION-DISABLED: Track history, analytics, comparison, detail, and replay routes
  {
    path: '/track/history',
    name: 'TrackHistory',
    component: () => import('@/views/track/TrackHistoryView.vue'),
    meta: {
      requiresAuth: true,
      title: 'tracks.trackHistory'
    }
  },
  {
    path: '/track/analytics',
    name: 'TrackAnalytics',
    component: () => import('@/views/track/TrackAnalyticsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'tracks.trackAnalytics'
    }
  },
  {
    path: '/track/compare',
    name: 'TrackComparison',
    component: () => import('@/views/track/TrackComparisonView.vue'),
    meta: {
      requiresAuth: true,
      title: 'tracks.compareTracks'
    }
  },
  {
    path: '/track/:id',
    name: 'TrackDetail',
    component: () => import('@/views/track/TrackDetailView.vue'),
    meta: {
      requiresAuth: true,
      title: 'tracking.trackDetails'
    }
  },
  {
    path: '/track/:id/replay',
    name: 'TrackReplay',
    component: () => import('@/views/track/TrackReplayView.vue'),
    meta: {
      requiresAuth: true,
      title: 'tracking.trackReplay'
    }
  },
  */
  {
    path: '/settings',
    redirect: '/tabs/profile'
  },
  {
    path: '/settings/accessibility',
    name: 'AccessibilityPreferences',
    component: () => import('@/views/settings/AccessibilityPreferencesView.vue'),
    meta: {
      requiresAuth: true,
      title: 'settings.accessibilityPreferences'
    }
  },
  {
    path: '/settings/notifications',
    name: 'NotificationSettings',
    component: () => import('@/views/settings/NotificationSettingsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'settings.notificationSettings'
    }
  },
  {
    path: '/settings/about',
    name: 'AboutApp',
    component: () => import('@/views/settings/AboutView.vue'),
    meta: {
      requiresAuth: true,
      title: 'settings.about'
    }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('@/views/help/HelpView.vue'),
    meta: {
      title: 'help.helpSupport'
    }
  },
  {
    path: '/help/contact',
    name: 'ContactSupport',
    component: () => import('@/views/help/ContactSupportView.vue'),
    meta: {
      requiresAuth: true,
      title: 'help.contactSupport'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/legal/PrivacyView.vue'),
    meta: {
      title: 'settings.privacyPolicy'
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/legal/TermsView.vue'),
    meta: {
      title: 'settings.termsOfService'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: {
      title: 'notFound.pageNotFound'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Set page title with i18n support
  if (to.meta.title) {
    const titleKey = to.meta.title as string
    try {
      // Ensure i18n.global is ready before accessing
      if (i18n && i18n.global) {
        // Check if translation key exists first
        const hasTranslation = i18n.global.te(titleKey)
        const appName = i18n.global.t('app.name', 'MATT')

        if (hasTranslation) {
          // Use translated title
          const translatedTitle = i18n.global.t(titleKey)
          document.title = `${translatedTitle} - ${appName}`
        } else {
          // Fallback to key as-is (for backwards compatibility)
          document.title = titleKey.includes(' - ') ? titleKey : `${titleKey} - ${appName}`
        }
      } else {
        // i18n not ready yet, use simple fallback
        document.title = titleKey.includes(' - ') ? titleKey : `${titleKey} - MATT`
      }
    } catch (error) {
      // Fallback in case of translation error
      document.title = titleKey.includes(' - ') ? titleKey : `${titleKey} - MATT`
    }
  }

  console.log('Router: Navigation guard executing:', {
    to: to.path,
    authenticated: authStore.isAuthenticated,
    user: authStore.user?.email
  })

  // Allow LoadingView to handle initial routing
  if (to.name === 'Root') {
    next()
    return
  }

  // Wait for auth initialization if user deep-linked directly to a protected route
  // (bypassing LoadingView which normally calls checkAuthStatus)
  if (!authStore.isAuthReady) {
    await authStore.checkAuthStatus()
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    console.log('Router: Route requires auth but user not authenticated')
    // Show session expired toast if user was previously in an authenticated area
    const comingFromAuthArea = from?.name && from?.meta?.requiresAuth
    if (comingFromAuthArea) {
      import('@ionic/vue').then(({ toastController }) => {
        toastController.create({
          message: i18n.global.t('auth.sessionExpired'),
          duration: 3500,
          color: 'warning',
          position: 'top'
        }).then(t => t.present())
      })
    }
    // Redirect to login with return URL
    next({
      name: 'Login',
      query: { redirect: to.fullPath }
    })
    return
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    console.log('Router: Route requires guest but user is authenticated')
    // Redirect authenticated users away from auth pages
    const redirectPath = (to.query.redirect as string) || '/tabs/home'
    next(redirectPath)
    return
  }

  // HIDDEN: Onboarding flow disabled - to be implemented later
  // Check if user needs onboarding (except if already going to onboarding)
  // if (to.meta.requiresAuth && to.name !== 'Onboarding' && authStore.user) {
  //   // Check if user has skipped onboarding
  //   const skippedUsers = await storageService.retrieve('onboarding_skipped') || {}
  //   const hasSkippedOnboarding = skippedUsers[authStore.user.id] === true

  //   if (!hasSkippedOnboarding) {
  //     const userNeedsOnboarding = needsOnboarding(authStore.user.profile)
  //     if (userNeedsOnboarding) {
  //       console.log('Router: User needs onboarding, redirecting...')
  //       next({ name: 'Onboarding' })
  //       return
  //     }
  //   }
  // }

  next()
})

// Handle authentication redirects - REMOVED problematic afterEach redirect
// Redirects are now handled properly in the login success flow

// Google Analytics 4 - Track page views for SPA route changes
router.afterEach((to) => {
  if (typeof window.gtag === 'function' && import.meta.env.VITE_GA_MEASUREMENT_ID) {
    window.gtag('event', 'page_view', {
      page_path: to.path,
      page_title: to.name?.toString() ?? to.path,
    })
  }
})

export default router